import { useDispatch, useSelector } from 'react-redux';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { useCallback, useEffect, useState } from 'react';
import { selectUser } from 'store/authSlice/authSelectors';
import { postCameraManual, CAMERA_MANUAL_PATH } from 'services/cameraDetectionService';

export const config = {
    disableTransactionAssignmentTimeout: 30000,
    assignToMePath: CAMERA_MANUAL_PATH,
    addToQueuePath: CAMERA_MANUAL_PATH,
    assignToMeRequestType: { missedDetection: 'MissedDetection', preview: 'Preview' },
};

const useTransactionAssignementActions = ({ cameraId, cameraHasTransaction, assignee, assignOnClick }) => {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);

    const [disableTransactionAssignment, setDisableTransactionAssignment] = useState(false);
    const isTransactionAssignmentDisabled = (cameraHasTransaction && !!assignee && assignee !== user?.username) || disableTransactionAssignment;

    const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
    const dropdownOpen = Boolean(dropdownAnchorEl);

    useEffect(() => {
        if (!cameraHasTransaction || (cameraHasTransaction && assignee === user?.username)) {
            setDisableTransactionAssignment(false);
        }
    }, [cameraHasTransaction, assignee, user?.username]);

    const handleClose = useCallback(() => {
        setDropdownAnchorEl(null);
    }, []);

    const handleAddToQueue = useCallback(async () => {
        try {
            handleClose();
            setDisableTransactionAssignment(true);
            const response = await postCameraManual(cameraId, { assignToMe: false, missedDetection: false });
            setTimeout(() => setDisableTransactionAssignment(false), config.disableTransactionAssignmentTimeout);
            if (response.status === 200) {
                dispatch(
                    snackbarActions.enqueueSnackbar({
                        message: `Successfuly added to queue transaction from camera with id ${cameraId}`,
                        options: { variant: 'success' },
                    })
                );
            } else {
                dispatch(
                    snackbarActions.enqueueSnackbar({
                        message: `${cameraId} ${response.error}`,
                        options: { variant: 'error' },
                    })
                );
            }
        } catch (error) {
            setDisableTransactionAssignment(false);
            dispatch(
                snackbarActions.enqueueSnackbar({
                    message: error.message,
                    options: { variant: 'error' },
                })
            );
        }
    }, [cameraId, dispatch, handleClose]);

    const handleAssignToMe = useCallback(
        async (requestType) => {
            try {
                handleClose();
                setDisableTransactionAssignment(true);
                console.log(`Sending TransactionAssignment-${requestType} request`);
                const response = await postCameraManual(cameraId, {
                    assignToMe: true,
                    missedDetection: requestType === config.assignToMeRequestType.missedDetection,
                });
                setTimeout(() => setDisableTransactionAssignment(false), config.disableTransactionAssignmentTimeout);
                if (response.status === 200) {
                    console.log(`TransactionAssignment-${requestType} request successful`);
                    dispatch(
                        snackbarActions.enqueueSnackbar({
                            message: `Successfuly assigned to you transaction from camera with id ${cameraId}`,
                            options: { variant: 'success' },
                        })
                    );
                } else {
                    console.log(`TransactionAssignment-${requestType} response status=${response.status}`);
                    dispatch(
                        snackbarActions.enqueueSnackbar({
                            message: `${cameraId} ${response.error}`,
                            options: { variant: 'error' },
                        })
                    );
                }
            } catch (error) {
                setDisableTransactionAssignment(false);
                console.log(`TransactionAssignment-${requestType} response error = ${JSON.stringify(error)}`);

                let message = `Error sending Transaction ${requestType} request`;
                const string = error.response?.data?.error;

                if (string) message = string?.substring(string.indexOf('[') + 1, string.indexOf(']'));

                if (message === 'DESK_INACTIVE') {
                    message = 'Transaction mode has to be enabled on Desks tab, in order for you to process the transaction';
                }

                dispatch(
                    snackbarActions.enqueueSnackbar({
                        message: message,
                        options: { variant: 'error' },
                    })
                );
            }
        },
        [cameraId, dispatch, handleClose]
    );

    const handlePreview = useCallback(
        (e) => {
            e?.stopPropagation();
            handleAssignToMe(config.assignToMeRequestType.preview);
        },
        [handleAssignToMe]
    );

    const handleClick = useCallback(
        (event) => {
            event.stopPropagation();
            if (assignOnClick) handlePreview();
            else setDropdownAnchorEl(event.currentTarget);
        },
        [assignOnClick, handlePreview]
    );

    const handleMissedDetection = useCallback(
        (e) => {
            e.stopPropagation();
            handleAssignToMe(config.assignToMeRequestType.missedDetection);
        },
        [handleAssignToMe]
    );

    return {
        anchorEl: dropdownAnchorEl,
        open: dropdownOpen,
        handleClick,
        handleClose,
        handlePreview,
        handleAddToQueue,
        handleMissedDetection,
        isTransactionAssignmentDisabled,
    };
};

export default useTransactionAssignementActions;
